import type { Session } from "@supabase/supabase-js";
import { CheckCircle2, CircleAlert, FileCode2, FilePlus2, Heart, LayoutList, LoaderCircle, LogOut, Settings, Sparkles, UserRound } from "lucide-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AutoDownloadCoordinator, SvgJobsRealtimeBridge, useAllSvgJobs } from "../features/svg-jobs/hooks";
import { isActive } from "../features/svg-jobs/status";
import { supabase } from "../lib/supabase";
import { useJobStore } from "../store/useJobStore";

const navItems = [
  { label: "创建任务", to: "/workspace", icon: FilePlus2, count: null },
  { label: "全部任务", to: "/jobs", icon: LayoutList, count: "all" },
  { label: "进行中", to: "/jobs?status=active", icon: LoaderCircle, count: "active" },
  { label: "已完成", to: "/jobs?status=completed", icon: CheckCircle2, count: "completed" },
  { label: "异常任务", to: "/jobs?status=failed", icon: CircleAlert, count: "failed" },
  { label: "收藏", to: "/jobs?status=favorite", icon: Heart, count: "favorite" },
  { label: "设置", to: "/settings", icon: Settings, count: null },
] as const;

function isActiveLink(pathname: string, search: string, target: string) {
  if (target.includes("?")) return `${pathname}${search}` === target;
  if (target === "/jobs") return pathname === "/jobs" && !search;
  return pathname === target;
}

export function AppLayout({ session }: { session: Session }) {
  const navigate = useNavigate();
  const location = useLocation();
  const jobsQuery = useAllSvgJobs();
  const jobs = jobsQuery.data || [];
  const realtimeState = useJobStore((state) => state.realtimeState);
  const counts = {
    all: jobs.length,
    active: jobs.filter(isActive).length,
    completed: jobs.filter((job) => job.status === "completed").length,
    failed: jobs.filter((job) => ["failed", "cancelled", "expired"].includes(job.status)).length,
    favorite: jobs.filter((job) => job.is_favorite).length,
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) return toast.error(error.message);
    navigate("/login", { replace: true });
  };

  return (
    <SvgJobsRealtimeBridge userId={session.user.id}>
      <AutoDownloadCoordinator jobs={jobs} />
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <div className="app-frame">
        <aside className="sidebar">
          <div className="sidebar-brand"><span className="brand-icon"><Sparkles size={18} /></span><div><strong>SVG Render</strong><small>多引擎绘图工作台</small></div></div>
          <div className="sidebar-section-label">工作区</div>
          <nav className="sidebar-nav" aria-label="任务导航">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActiveLink(location.pathname, location.search, item.to);
              return <Link key={item.to} className={`sidebar-link ${active ? "active" : ""}`} to={item.to} aria-current={active ? "page" : undefined}><Icon className={item.label === "进行中" && counts.active > 0 ? "spin" : ""} size={17} /><span>{item.label}</span>{item.count && <b>{counts[item.count]}</b>}</Link>;
            })}
          </nav>
          <div className="sidebar-footer">
            <div className={`realtime-state state-${realtimeState}`}><span />{realtimeState === "connected" ? "实时同步" : realtimeState === "fallback" ? "轮询兜底" : "正在连接"}</div>
            <div className="account-card"><span><UserRound size={16} /></span><div><small>当前账号</small><strong>{session.user.email || "已登录用户"}</strong></div><button className="icon-button" onClick={() => void signOut()} aria-label="退出登录"><LogOut size={16} /></button></div>
          </div>
        </aside>
        <div className="app-content">
          <header className="mobile-header"><Link to="/workspace" className="mobile-brand"><FileCode2 size={18} />SVG Render</Link><Link className="secondary-button" to="/jobs">任务</Link></header>
          <main id="main-content" tabIndex={-1}><Outlet /></main>
        </div>
      </div>
    </SvgJobsRealtimeBridge>
  );
}
